import React, { useEffect, useMemo } from 'react'

import useUrlState from '@ahooksjs/use-url-state'
import { getValueFormat } from '@baurine/grafana-value-formats'
import {
  Alert,
  Button,
  Card,
  Group,
  Select,
  Skeleton,
  Stack,
  Table,
  Text
} from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { DatePicker } from 'antd'
import moment from 'moment-timezone'

import { listClusterFiles } from '~/api'
import { useCurCluster, useCurOrg } from '~/hooks/state'
import { useMutation } from '~/hooks/use-mutation'
import { clusterFilesTelemetry } from '~/telemetry/cluster-files'
import { formatDateTimeWithTz } from '~/utils/date-time'

const defQueryParams = {
  fileType: 'oom_record',
  beginTime: 'now-24h',
  endTime: 'now',
  notEmpty: 'false'
}

const FILE_TYPES: { value: string; label: string }[] = [
  { value: 'oom_record', label: 'OOM Files' },
  {
    value: 'plan_replayer',
    label: 'Plan Replayer'
  }
]

const Page: React.FC = () => {
  const { curOrg } = useCurOrg()
  const { curCluster } = useCurCluster()

  const [queryParams, setQueryParams] = useUrlState(defQueryParams)
  const { beginTime, endTime } = queryParams
  const timeRangeKey = useMemo(() => {
    if (endTime === 'now' && beginTime.startsWith('now')) {
      return beginTime
    }
    // Number('now') = NaN
    // Number('') = 0
    if (Number(beginTime) >= 0 && Number(endTime) >= 0) {
      return 'absolute'
    }
    return 'now-24h'
  }, [beginTime, endTime])

  const defTimeRange = useMemo<[moment.Moment, moment.Moment] | null>(() => {
    if (timeRangeKey === 'absolute' && beginTime !== '' && endTime !== '') {
      return [moment.unix(Number(beginTime)), moment.unix(Number(endTime))]
    }
    return null
  }, [timeRangeKey, beginTime, endTime])

  function handleTimeChange(values: any) {
    if (values === null) {
      setQueryParams({
        beginTime: '',
        endTime: '',
        notEmpty: 'false'
      })
    } else {
      const beginTime = values[0].unix()
      const endTime = values[1].unix()
      setQueryParams({
        beginTime: `${beginTime}`,
        endTime: `${endTime}`,
        notEmpty: 'false'
      })
    }
  }

  const enableQuery = beginTime !== '' && endTime !== ''

  // TODO: rename useMutation to useRequestWithoutCache
  const listFilesRequest = useMutation(listClusterFiles)
  function handleQuery() {
    listFilesRequest.run({
      fileType: queryParams.fileType,
      beginTime,
      endTime,
      cluster: curCluster!,
      org: curOrg!
    })
  }
  useEffect(() => {
    if (beginTime !== '' || endTime !== '') {
      handleQuery()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const files = useMemo(() => {
    let _files: any[] = []
    const records = listFilesRequest.data?.data.records ?? []
    records.forEach((r: any) => {
      r.files.forEach((f: any) => {
        _files.push({
          ...f,
          instance: r.instance,
          friendSize: getValueFormat('decbytes')(f.size, 1),
          friendCreateTime: formatDateTimeWithTz(f.created_at_unix_sec)
        })
      })
    })
    return _files.sort((a, b) => b.created_at_unix_sec - a.created_at_unix_sec)
  }, [listFilesRequest.data])

  return (
    <Card withBorder mih={400}>
      <Stack spacing='xl'>
        <Group className='items-end'>
          <Select
            label='Files Type'
            data={FILE_TYPES}
            defaultValue={queryParams.fileType}
            onChange={(v) => setQueryParams({ fileType: v, notEmpty: 'false' })}
          />

          <Select
            label='Time Range'
            data={[
              { value: 'now-24h', label: 'Recent 24 hours' },
              { value: 'now-2d', label: 'Recent 2 days' },
              { value: 'now-7d', label: 'Recent 7 days' },
              { value: 'absolute', label: 'Absolute' }
            ]}
            defaultValue={timeRangeKey}
            onChange={(v) => {
              clusterFilesTelemetry.selectTimeRange({ time_range_type: v! })
              if (v !== 'absolute') {
                setQueryParams({
                  beginTime: v,
                  endTime: 'now',
                  notEmpty: 'false'
                })
              } else {
                setQueryParams({
                  beginTime: '',
                  endTime: '',
                  notEmpty: 'false'
                })
              }
            }}
          />

          {timeRangeKey === 'absolute' && (
            <DatePicker.RangePicker
              showTime
              className='rounded-[4px] h-9'
              onChange={handleTimeChange}
              defaultValue={defTimeRange}
            />
          )}

          <Button
            disabled={!enableQuery}
            loading={listFilesRequest.loading}
            onClick={() => {
              handleQuery()
              clusterFilesTelemetry.clickQuery()
            }}
          >
            Query
          </Button>
        </Group>

        <Skeleton visible={listFilesRequest.loading} animate>
          <Text color='dimmed'>
            {files.length} {files.length === 1 ? 'file' : 'files'}, show up to
            100 files.
          </Text>

          {listFilesRequest.data &&
            files.length === 0 &&
            queryParams.notEmpty === 'true' && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color='red'
                className='my-2'
              >
                It's expected to have non emtpy results, please inquire at Ask
                Clinic Group.
              </Alert>
            )}

          <Table horizontalSpacing={0} verticalSpacing='sm'>
            <thead>
              <tr>
                <th>Instance</th>
                <th>File</th>
                <th>Size</th>
                <th>Create Time</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr key={f.key}>
                  <td>{f.instance}</td>
                  <td>{f.filename}</td>
                  <td>{f.friendSize}</td>
                  <td>{f.friendCreateTime}</td>
                  <td>
                    <Text
                      className='underline'
                      component='a'
                      href={f.download_url}
                      onClick={() => clusterFilesTelemetry.clickDownload()}
                    >
                      Download
                    </Text>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Skeleton>
      </Stack>
    </Card>
  )
}

export default Page
